// ═══════════════════════════════════════════════════════════
//  blogDetailsData.js — Full article content for each post
//  Keyed by blog post id (matching blogsData.js)
// ═══════════════════════════════════════════════════════════

const blogDetailsData = {

  // ─────────────────────────────────────────────────────────
  // Post 1: 25 n8n Tips and Hidden Features
  // ─────────────────────────────────────────────────────────
  "1": `
## Introduction

Automation platforms like n8n are powerful, but the difference between a beginner and an expert often lies in knowing the small shortcuts and hidden settings that save time. This guide breaks down **25 essential tips and hidden features** that help you build, test, and deploy automations faster and more reliably.

---

## Getting Started: Requirements

Before diving in, make sure you have:

- An active n8n instance (Desktop, Cloud, or self-hosted via Docker)
- Access to the workflow editor
- Basic familiarity with nodes and connections

---

## Tips 1–5: Workflow Organization

### 1. Use Sticky Notes for Documentation
Add **Sticky Note** nodes to your workflows to document complex logic. This makes it easy for teammates (or future-you) to understand what each section does.

### 2. Color-Code Your Nodes
Right-click any node → **Change Color** to visually group related nodes. Use green for triggers, blue for data processing, and red for error handlers.

### 3. Use Sub-Workflows for Reusability
Break large workflows into smaller **sub-workflows** using the **Execute Workflow** node. This keeps things modular and DRY (Don't Repeat Yourself).

\`\`\`
Main Workflow → Execute Sub-Workflow → Return result
\`\`\`

### 4. Pin Data for Testing
During development, **pin the output** of a node to freeze its data. This lets you tweak downstream nodes without re-triggering the whole workflow.

### 5. Name Your Nodes Descriptively
Instead of "HTTP Request", name it "Fetch User from Stripe". Clear node names make debugging 10x faster.

---

## Tips 6–10: Expressions & Variables

### 6. Master the Expression Editor
Use \`{{ }}\` expressions to reference data from previous nodes:

\`\`\`js
{{ $node["Get User"].json["email"] }}
\`\`\`

### 7. Use \$json Shorthand
In the current node context, \`$json\` refers to the current item's data — saving you from writing long node-path expressions.

### 8. Date Manipulation with Luxon
n8n has **Luxon** built-in for date operations:

\`\`\`js
{{ DateTime.now().minus({ days: 7 }).toISO() }}
\`\`\`

### 9. Store Reusable Values in Global Variables
Use the **Set** node at the start of your workflow to define constants (API base URLs, thresholds) once and reference them throughout.

### 10. Use \$env for Credentials
Store sensitive values as environment variables and reference them with \`$env.MY_SECRET\` instead of hardcoding them.

---

## Tips 11–15: Error Handling

### 11. Add Error Workflow
In **Workflow Settings → Error Workflow**, point to a dedicated error-handling workflow that sends Slack/email alerts on failure.

### 12. Use the Try/Catch Pattern
Wrap risky nodes (HTTP calls, DB queries) with **Error Trigger** paths to handle failures gracefully without stopping execution.

### 13. Always Set Retry on Failure
For HTTP Request nodes, enable **Retry on Fail** with a delay to handle transient API errors automatically.

### 14. Log Failed Items Separately
Use an **IF** node after batch operations to route failed items to a separate error log (Airtable, Google Sheets, etc.).

### 15. Use the Execution Log
Check **Executions → Failed** regularly. n8n stores full input/output for each run — invaluable for debugging.

---

## Tips 16–20: Performance

### 16. Use Batch Processing
When handling large datasets, use the **Loop Over Items** node with a batch size of 10–50 to avoid memory issues.

### 17. Avoid Polling When Possible
Replace polling triggers (every 1 min) with **webhooks** wherever the service supports it. Webhooks are faster and use fewer resources.

### 18. Use Wait Nodes Strategically
Add **Wait** nodes between API calls that have rate limits instead of hitting the limit and failing.

### 19. Parallelize with Split in Batches
For independent operations (sending emails to a list), use **Split In Batches** to process multiple items simultaneously.

### 20. Disable Unused Workflows
Inactive triggered workflows still consume resources. Deactivate any workflow you're not currently using.

---

## Tips 21–25: Advanced Features

### 21. Use AI Nodes (LangChain Integration)
n8n has built-in **AI Agent** and **Chat** nodes powered by LangChain. You can build RAG pipelines without writing a single line of Python.

### 22. Leverage the Community Nodes
Install community nodes from **Settings → Community Nodes**. There are 400+ community-built integrations not in the official list.

### 23. Use the HTTP Request Tool Node
The **HTTP Request** node supports OAuth2, custom headers, form data, file uploads — it's the Swiss Army knife of n8n.

### 24. Version Control with Git
Export workflows as JSON and commit them to a Git repo for version history and team collaboration.

\`\`\`bash
# Export all workflows
n8n export:workflow --all --output=./workflows/
\`\`\`

### 25. Monitor with Prometheus & Grafana
n8n exposes a \`/metrics\` endpoint. Connect it to **Prometheus + Grafana** for real-time monitoring of executions, failures, and queue depth.

---

## Summary

Mastering these 25 tips will dramatically improve your n8n workflows — from cleaner organization and smarter expressions to robust error handling and production-grade performance. Start with the ones most relevant to your current pain points and work through the rest over time.

> **Pro tip:** Bookmark this guide and revisit it as your n8n skills grow — the advanced tips will make more sense as you gain experience.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 2: 18 n8n Tips and Hacks
  // ─────────────────────────────────────────────────────────
  "2": `
## Introduction

As workflows grow in complexity, they can become slow, difficult to manage, or expensive to run. This guide compiles **18 essential tips and hacks** focused on improving execution speed, integrating AI, and maintaining a clean professional workspace.

---

## Requirements

- n8n instance (v1.0+)
- At least one active workflow
- Optional: Docker setup for self-hosted deployment

---

## Optimization Tips

### 1. Use Sub-Workflows to Modularize
Split monolithic workflows into smaller sub-workflows. Each sub-workflow does one thing well, making debugging and reuse trivial.

### 2. Reduce Node Count with Function Nodes
Replace chains of **Set**, **Move Binary Data**, and **Merge** nodes with a single **Code** node that handles the transformation in JavaScript.

\`\`\`js
// Transform items in one Code node
return items.map(item => ({
  json: {
    name: item.json.firstName + ' ' + item.json.lastName,
    email: item.json.email.toLowerCase()
  }
}));
\`\`\`

### 3. Cache API Responses
Use a **Redis** or **Airtable** cache node to store expensive API responses. Check the cache first; only call the API on a miss.

### 4. Schedule During Off-Peak Hours
For non-urgent batch jobs, schedule them at 2–4 AM when API rate limits are less contested and your server load is low.

### 5. Use Incremental Syncs
Instead of fetching all records every run, store the last sync timestamp and use it to fetch only **new/updated records**.

\`\`\`js
// Store last sync time
const lastSync = $node["Get Last Sync"].json["timestamp"];
const newRecords = allRecords.filter(r => r.updatedAt > lastSync);
\`\`\`

---

## AI Integration Tips

### 6. Connect OpenAI for Intelligent Routing
Use an **AI Agent** node to classify incoming data (support tickets, emails) and route them to different workflow branches automatically.

### 7. Build a RAG Pipeline
Connect a **Vector Store** node (Pinecone, Supabase) with the **Question & Answer Chain** node to build a knowledge base chatbot in minutes.

### 8. Use AI for Data Cleaning
Feed messy input data to an AI node to extract structured information (names, dates, addresses) before processing.

### 9. Generate Dynamic Content
Use the **OpenAI** node to generate personalized email content, product descriptions, or summaries on the fly inside your workflows.

### 10. Monitor AI Costs
Track token usage by logging the \`usage\` object returned by OpenAI calls:

\`\`\`js
const tokens = $node["OpenAI"].json["usage"]["total_tokens"];
// Log to Google Sheets or Airtable
\`\`\`

---

## Workspace Cleanliness

### 11. Use Folders to Organize Workflows
Group workflows by project or team using **Folders** in the n8n dashboard. Name them consistently: \`[Team] Project - Workflow Name\`.

### 12. Archive Old Workflows
Don't delete old workflows — export them as JSON, store in a GitHub repo, and deactivate them. You might need them as reference later.

### 13. Standardize Naming Conventions
Agree on a naming convention for your team:
- Triggers: \`TRIGGER - Description\`
- Main flows: \`MAIN - Description\`  
- Sub-flows: \`SUB - Description\`

### 14. Document with README Sticky Notes
Add a large **Sticky Note** at the top of every workflow with:
- Purpose
- Owner
- Last updated date
- Dependencies

### 15. Use Tags for Searchability
Tag workflows with relevant labels (\`production\`, \`staging\`, \`deprecated\`) to filter them quickly in the dashboard.

---

## Advanced Hacks

### 16. Trigger Workflows via CLI
Use the n8n CLI to trigger workflows programmatically from scripts or CI/CD pipelines:

\`\`\`bash
n8n execute --id <workflow-id>
\`\`\`

### 17. Use Webhook IDs for Stable URLs
By default, webhook URLs change when you duplicate a workflow. Set a custom **Webhook Path** to keep URLs stable across environments.

### 18. Self-Host for Maximum Control
Running n8n self-hosted with Docker gives you:
- No execution limits
- Full data privacy
- Custom domain and SSL
- Persistent SQLite or Postgres database

\`\`\`yaml
# docker-compose.yml snippet
services:
  n8n:
    image: n8nio/n8n
    ports:
      - "5678:5678"
    environment:
      - N8N_BASIC_AUTH_ACTIVE=true
\`\`\`

---

## Conclusion

These 18 tips cover the full spectrum — from raw performance gains to AI integration and workspace hygiene. Apply them incrementally, starting with the ones that address your biggest pain points today.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 3: Facebook Graph API to n8n
  // ─────────────────────────────────────────────────────────
  "3": `
## Introduction

By connecting the **Facebook Graph API** to n8n, you can automate tasks like publishing posts, managing comments, and tracking engagement — without writing extensive custom code. This guide walks you through every step of the setup process.

---

## Prerequisites

Before you begin, make sure you have:

- An active **Meta Developer** account at [developers.facebook.com](https://developers.facebook.com)
- A Facebook **Page** (not a personal profile)
- An n8n instance (Cloud or self-hosted)
- Basic understanding of REST APIs

---

## Step 1: Create a Meta App

1. Go to [Meta for Developers](https://developers.facebook.com/apps)
2. Click **Create App**
3. Select **Business** as the app type
4. Fill in your app name and contact email
5. Click **Create App**

---

## Step 2: Add Facebook Login & Pages API

Once your app is created:

1. In the **App Dashboard**, click **Add Product**
2. Select **Facebook Login** → click **Set Up**
3. Also add **Pages API** from the product list

---

## Step 3: Generate a Page Access Token

This is the most critical step:

1. Go to **Tools → Graph API Explorer**
2. In the top-right, select your app
3. Click **Generate Access Token**
4. Grant the following permissions:
   - \`pages_show_list\`
   - \`pages_read_engagement\`
   - \`pages_manage_posts\`
5. Click **Get User Access Token**
6. Exchange for a **Page Access Token** using:

\`\`\`bash
GET https://graph.facebook.com/v18.0/me/accounts?access_token=USER_TOKEN
\`\`\`

The response includes your Page's \`access_token\`. **Save it.**

---

## Step 4: Configure n8n HTTP Request Node

In n8n, add an **HTTP Request** node with these settings:

| Field | Value |
|---|---|
| Method | POST |
| URL | \`https://graph.facebook.com/v18.0/{page-id}/feed\` |
| Authentication | None (token in body) |
| Body Type | JSON |

**Body:**
\`\`\`json
{
  "message": "Hello from n8n! 🚀",
  "access_token": "YOUR_PAGE_ACCESS_TOKEN"
}
\`\`\`

---

## Step 5: Test the Connection

1. Run the node manually in n8n
2. Check your Facebook Page — the post should appear within seconds
3. If you get a \`200 OK\` with a \`post_id\`, it worked! ✅

---

## Step 6: Automate with a Schedule Trigger

Add a **Schedule Trigger** node to post automatically:

\`\`\`
[Schedule: Every Monday 9AM] → [Set Content] → [HTTP Request to Facebook]
\`\`\`

---

## Common Errors & Fixes

| Error | Cause | Fix |
|---|---|---|
| \`OAuthException\` | Invalid token | Re-generate Page Access Token |
| \`#200\` Permission Error | Missing permissions | Re-add permissions in Explorer |
| Token expired | Short-lived token | Exchange for long-lived token (60 days) |

---

## Extending Long-Lived Tokens

User tokens expire in 1 hour. Exchange for a 60-day token:

\`\`\`bash
GET https://graph.facebook.com/v18.0/oauth/access_token
  ?grant_type=fb_exchange_token
  &client_id={app-id}
  &client_secret={app-secret}
  &fb_exchange_token={short-lived-token}
\`\`\`

---

## What You Can Automate Next

- 📊 **Pull page insights** — reach, engagement, impressions
- 💬 **Reply to comments** automatically using AI
- 📸 **Publish photo posts** with the \`/photos\` endpoint
- 📅 **Schedule posts** by setting the \`scheduled_publish_time\` field

---

## Conclusion

Connecting the Facebook Graph API to n8n unlocks powerful social media automation. Once your token is set up, you can build sophisticated posting, moderation, and analytics workflows without a single line of manual code.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 4: Manus AI Use Cases
  // ─────────────────────────────────────────────────────────
  "4": `
## Introduction

Manus AI represents the next evolution in AI — an agent that doesn't just *advise*, but *executes*. At **$39/month**, it functions as a 24/7 digital employee capable of browsing the web, clicking buttons, filling forms, and managing complex multi-step workflows autonomously.

This guide covers **7 practical use cases** to help you automate your business and daily life with Manus AI.

---

## What Makes Manus AI Different?

| Tool | Type | Can Execute Tasks? |
|---|---|---|
| ChatGPT | Conversational AI | ❌ (advice only) |
| Claude | Conversational AI | ❌ (advice only) |
| Manus AI | **Agentic AI** | ✅ Yes |

Manus AI has a **live browser**, file system access, and the ability to run code — making it a true autonomous agent.

---

## Use Case 1: Automated Lead Generation

**The Problem:** Manually finding leads from LinkedIn, websites, or directories takes hours.

**Manus Solution:**
1. Give Manus a target profile (e.g., "SaaS founders in Austin, TX")
2. It searches LinkedIn, Crunchbase, and Twitter
3. Extracts: name, company, email, LinkedIn URL
4. Exports a clean CSV to your Google Drive

> "Find 50 B2B SaaS founders in Texas who raised Series A in 2025. Get their LinkedIn profiles, company names, and emails if public. Export to a spreadsheet."

**Time saved:** 4–6 hours → 20 minutes

---

## Use Case 2: Competitor Research & Analysis

Manus can perform deep competitor analysis on autopilot:

- Visit competitor websites and capture pricing pages
- Read their blog and summarize recent content themes
- Check their G2/Trustpilot reviews for common complaints
- Compile a comparative report with your positioning recommendations

\`\`\`
Prompt: "Analyze our top 3 competitors: Acme, Globex, and Initech. 
Compare their pricing, key features, and recent product updates. 
Summarize strengths and weaknesses in a table."
\`\`\`

---

## Use Case 3: AI-Powered Headhunting

**Traditional approach:** Post job → Wait → Sift through 200 applications.

**Manus approach:**
1. Define your ideal candidate profile
2. Manus searches LinkedIn, GitHub, and job boards
3. Scores candidates based on your criteria
4. Drafts personalized outreach emails for top matches
5. Sends you a shortlist with reasoning

**Time saved:** 8–10 hours of recruiting work per role

---

## Use Case 4: Market Research Reports

Need a thorough market analysis without paying a consultant?

Give Manus a research prompt and it will:
- Read industry reports (Gartner, McKinsey PDFs)
- Scrape relevant news articles
- Analyze trends, market size, and key players
- Generate a structured 10-page report with citations

---

## Use Case 5: Automated Content Repurposing

Turn one long-form piece into multiple content formats:

\`\`\`
Input: Blog post URL or PDF

Output:
- Twitter/X thread (10 tweets)
- LinkedIn post (professional tone)
- Short YouTube script (2-3 min)
- Email newsletter snippet
- 5 Instagram caption options
\`\`\`

Manus handles the entire transformation in one task.

---

## Use Case 6: Personal Research Assistant

Use Manus for personal productivity:

- **Flight booking research** — find the cheapest routes for your dates
- **Restaurant discovery** — find top-rated restaurants matching your dietary requirements in a new city
- **Product comparisons** — compare 5 laptops across 10 criteria and recommend the best one for your use case
- **Learning paths** — create a 30-day study plan for any topic, with curated resources

---

## Use Case 7: Business Process Documentation

Manus can observe and document your existing processes:

1. Record a Loom of you doing a manual task
2. Share the video with Manus
3. It generates a step-by-step SOP with screenshots
4. Exports as a formatted Notion page or PDF

This is invaluable for onboarding new employees or handing off tasks.

---

## Limitations to Know

- **Not always accurate** — verify critical outputs before acting
- **Session limits** — complex tasks may time out
- **Privacy** — don't give it access to sensitive credentials
- **Internet-dependent** — real-time web access can be inconsistent

---

## Conclusion

Manus AI is the closest thing to a tireless, capable digital employee available today. For $39/month, the ROI is clear if you can identify the right repetitive, research-heavy, or browser-based tasks in your workflow. Start with one use case, verify the output quality, then scale from there.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 5: 8 Expert Secrets to Master Claude Code
  // ─────────────────────────────────────────────────────────
  "5": `
## Introduction

When Anthropic accidentally leaked the Claude Code source code through an npm package source map, developers got an unprecedented look inside one of the most powerful AI coding tools available. Over **500,000 lines of production-grade code** were exposed, revealing sophisticated architecture that most users never knew existed.

This guide distills the 8 biggest insights from that leak into actionable techniques you can apply today.

---

## What is Claude Code?

Claude Code is a **command-line AI coding agent** built by Anthropic. Unlike web chatbots, it lives in your terminal and can:

- Read, write, and modify files directly
- Run shell commands and tests
- Coordinate multiple AI sub-agents
- Maintain persistent memory across sessions

---

## Requirements for Success

To get the most out of Claude Code, ensure:

- **Node.js and npm** installed for package management
- **Bun runtime** for high-performance execution
- A well-structured project directory
- An **Anthropic API key** with sufficient credits
- A \`claude.md\` file in your project root (see Secret #3)

---

## Secret 1: The Memory System is Multi-Layered

The leak revealed Claude Code uses **4 distinct memory layers**:

| Layer | Type | Purpose |
|---|---|---|
| In-context | Temporary | Current conversation |
| External files | Persistent | \`claude.md\` notes |
| Vector store | Semantic | Code embeddings |
| Episodic | Long-term | Past session summaries |

**Action:** Use \`/compact\` frequently to compress context and reduce token costs while preserving important information.

---

## Secret 2: Hidden Slash Commands

Beyond the documented commands, the source revealed several power-user commands:

\`\`\`bash
/init      # Creates claude.md — initializes project context
/plan      # Forces planning mode before code changes
/compact   # Compresses conversation history
/review    # Triggers structured code review workflow
/context   # Specify which files Claude should focus on
/cost      # Real-time token cost breakdown
/resume    # Pick up work from a previous session
/summary   # Summarize the current session
\`\`\`

> **Pro tip:** Always start sessions with \`/resume\` and end them with \`/summary\` to maintain continuity across days.

---

## Secret 3: The \`claude.md\` File is Critical

This file is Claude Code's **long-term memory**. The source code shows it's loaded at the start of every session and deeply influences behavior.

Create a \`claude.md\` in your project root:

\`\`\`markdown
# Project: My SaaS App

## Stack
- Frontend: React 19 + TypeScript
- Backend: Node.js + Express
- Database: PostgreSQL + Prisma

## Coding Standards
- Always use TypeScript strict mode
- Prefer functional components over class components
- Use pnpm for dependency management

## Architecture Notes
- Auth handled by /services/auth.ts
- All API calls go through /utils/apiClient.ts
- Never commit .env files
\`\`\`

The more context you provide here, the less you need to repeat yourself.

---

## Secret 4: The Tool System Architecture

The leak shows Claude Code has a sophisticated **tool system** with:

- **File operations** — read, write, edit, delete
- **Shell operations** — run commands, scripts, tests
- **Search tools** — grep, find, semantic search
- **Browser tools** — fetch URLs, read web pages
- **MCP tools** — connect external services

Each tool has strict **permission gates** — Claude must request and receive approval before executing dangerous operations.

---

## Secret 5: Multi-Agent Coordination

One of the biggest surprises: Claude Code can spawn and manage **sub-agents** for parallel work.

\`\`\`
Main Agent
├── Sub-agent A: Write unit tests
├── Sub-agent B: Update documentation
└── Sub-agent C: Refactor utility functions
\`\`\`

Use this by describing parallel workstreams: *"While you're implementing the API, also have an agent write tests for the existing endpoints."*

---

## Secret 6: The Permission Engine

Claude Code has a granular **permission engine** that gates:

- File writes outside the project directory
- Shell command execution
- Network requests
- External database connections

**Implication:** You can safely run Claude Code in your project without worrying about it accidentally modifying system files.

---

## Secret 7: Context Management is an Art

Token costs add up fast. The source revealed these context optimization strategies:

1. **Use \`/context\`** to narrow Claude's focus to relevant files only
2. **Use \`/compact\`** before switching tasks to compress history
3. **Split long sessions** — use \`/summary\` then start fresh
4. **Use \`/plan\` first** — planning before coding saves tokens overall

\`\`\`bash
# Good session management pattern
/resume                    # Load previous context
/context src/api/*.ts      # Focus on relevant files
[do your work]
/cost                      # Check spend
/compact                   # Compress when context grows
/summary                   # Save session summary before closing
\`\`\`

---

## Secret 8: Connect External Knowledge via MCP

The source revealed **Model Context Protocol (MCP)** integration that lets Claude Code connect to:

- External databases (read your actual schema)
- Private APIs (your internal docs)
- Company wikis and documentation
- Monitoring tools (Datadog, Sentry)

This transforms Claude Code from a generic coding assistant into one that **knows your specific system** deeply.

\`\`\`json
// .claude/mcp.json
{
  "servers": {
    "postgres": {
      "command": "npx",
      "args": ["@anthropic/mcp-server-postgres", "postgresql://localhost/mydb"]
    }
  }
}
\`\`\`

---

## Conclusion

The source code leak revealed that Claude Code is far more sophisticated than its surface-level UI suggests. By leveraging the layered memory system, hidden commands, and MCP integrations, you can transform it from a smart autocomplete into a genuine autonomous coding partner.

Start with **Secret #3** (the \`claude.md\` file) — it requires no extra setup and immediately improves every session.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 6: Claude Code Routines
  // ─────────────────────────────────────────────────────────
  "6": `
## Introduction

Anthropic's latest addition to Claude Code — **Routines** — marks a fundamental shift from reactive to proactive AI assistance. Instead of waiting for you to type a prompt, Routines allow Claude Code to perform complex engineering tasks **on a schedule or in response to triggers**, entirely autonomously.

Released in research preview, this feature turns Claude Code from a coding assistant into a genuine background worker.

---

## What Are Claude Code Routines?

Routines are **scheduled or event-driven workflows** that Claude Code executes without human intervention. Think of them as cron jobs, but with AI-level intelligence about your codebase.

Examples:
- Every morning at 8 AM: Review new GitHub issues and triage them
- On every PR merge: Update the CHANGELOG and bump version numbers
- Every Sunday: Generate a weekly code quality report
- On new failing test: Automatically investigate and attempt a fix

---

## How Routines Work

\`\`\`
Trigger (schedule / event)
    ↓
Claude Code loads context (claude.md + repo state)
    ↓
Executes task with full tool access (files, shell, git)
    ↓
Creates PR / commits / sends notification
    ↓
Awaits human review (optional approval gate)
\`\`\`

---

## Setting Up Your First Routine

### Step 1: Create a Routines Config

In your project root, create \`.claude/routines.json\`:

\`\`\`json
{
  "routines": [
    {
      "name": "Daily Issue Triage",
      "schedule": "0 8 * * 1-5",
      "task": "Review all GitHub issues opened in the last 24 hours. Label them by type (bug, feature, docs). Add an initial response comment. Create a triage summary in TRIAGE.md.",
      "approval_required": false
    },
    {
      "name": "Weekly Code Review",
      "schedule": "0 9 * * 1",
      "task": "Analyze the diff from the past week. Identify code smells, security issues, and performance bottlenecks. Create a detailed report in docs/weekly-review.md.",
      "approval_required": true
    }
  ]
}
\`\`\`

### Step 2: Authenticate with GitHub

\`\`\`bash
claude auth github --repo your-org/your-repo
\`\`\`

### Step 3: Deploy the Routine

\`\`\`bash
claude routines deploy
\`\`\`

### Step 4: Monitor Execution

\`\`\`bash
claude routines logs --routine "Daily Issue Triage"
\`\`\`

---

## Routine Types

### 1. Scheduled Routines (Cron)

Run on a time-based schedule using standard cron syntax:

\`\`\`
"0 9 * * 1"    → Every Monday at 9 AM
"*/30 * * * *" → Every 30 minutes
"0 0 * * *"    → Every day at midnight
\`\`\`

### 2. Event-Driven Routines

Trigger on GitHub events:

\`\`\`json
{
  "name": "PR Review Assistant",
  "trigger": {
    "type": "github_event",
    "event": "pull_request.opened"
  },
  "task": "Review this PR for: correctness, test coverage, code style, and security. Post a detailed review comment."
}
\`\`\`

### 3. Conditional Routines

Only run when a condition is met:

\`\`\`json
{
  "name": "Fix Failing Tests",
  "trigger": {
    "type": "ci_failure",
    "branch": "main"
  },
  "task": "Investigate the failing tests. Attempt to fix them. If fixed, open a PR. If not, open an issue with full diagnostic report."
}
\`\`\`

---

## Real-World Routine Examples

### Automated Changelog Generator
\`\`\`
Schedule: On every version tag push
Task: Parse git log since last tag, categorize commits (feat/fix/docs/chore), 
      generate formatted CHANGELOG.md entry, commit and push.
\`\`\`

### Dependency Security Scanner
\`\`\`
Schedule: Every Monday 7 AM
Task: Run npm audit, check for high/critical vulnerabilities, 
      attempt auto-fix where possible, create GitHub issue for manual fixes.
\`\`\`

### Documentation Staleness Checker
\`\`\`
Schedule: Every Friday
Task: Compare docs/ folder with recent code changes. Flag documentation 
      that references deleted functions or outdated APIs. Create update PRs.
\`\`\`

---

## Safety & Approval Gates

Routines can be configured to require human approval before taking action:

\`\`\`json
{
  "approval_required": true,
  "approval_channel": "slack",
  "slack_channel": "#engineering-approvals",
  "timeout_hours": 24
}
\`\`\`

With this config, Claude Code will:
1. Complete the analysis/draft
2. Post a Slack message with a summary and diff
3. Wait for a ✅ or ❌ reaction
4. Execute or cancel based on the response

---

## Best Practices

1. **Start with read-only routines** — analysis and reporting before any writes
2. **Always set approval gates** for routines that modify code or open PRs
3. **Keep tasks focused** — one clear objective per routine
4. **Monitor the first 5 runs** of any new routine closely
5. **Use \`dry_run: true\`** in config to test without side effects

---

## Current Limitations (Research Preview)

- Maximum routine duration: **30 minutes**
- Maximum concurrent routines: **3**
- GitHub is the only supported VCS (GitLab coming soon)
- No support for private npm packages in the routine environment
- Logs retained for **7 days**

---

## Conclusion

Claude Code Routines represent a genuine leap toward autonomous software development. When configured thoughtfully, they can handle hours of routine engineering work — freeing your team to focus on creative problem-solving and architecture decisions.

Start with a simple **weekly code review routine** to get comfortable with the system, then gradually add more sophisticated automations as you build trust in the output quality.

> The future of software development isn't about AI replacing developers — it's about AI handling the *routine* so developers can tackle the *remarkable*.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 7: How to Set Up OpenClaw
  // ─────────────────────────────────────────────────────────
  "7": `
## Introduction

**OpenClaw** is an open-source autonomous AI agent framework that you can self-host on your own VPS. Unlike cloud-only AI tools, OpenClaw gives you full control — over the model, the data, and the execution environment. This guide walks you through a complete setup from a blank server to a fully operational AI agent with Telegram integration.

---

## What is OpenClaw?

OpenClaw is a modular autonomous agent that can:

- Browse the web and extract information
- Write and execute code
- Manage files and databases
- Communicate via Telegram or webhook
- Run skills (custom plugins) to extend its capabilities

---

## Requirements

- A VPS with at least **2 vCPU / 4 GB RAM** (DigitalOcean, Hetzner, or Linode recommended)
- Ubuntu 22.04 LTS
- A **Telegram Bot Token** (from [@BotFather](https://t.me/botfather))
- An **OpenAI or Anthropic API key**
- Basic Linux command-line familiarity

---

## Step 1: Provision Your VPS

\`\`\`bash
# Update system packages
sudo apt update && sudo apt upgrade -y

# Install required tools
sudo apt install -y git curl python3-pip python3-venv nodejs npm
\`\`\`

---

## Step 2: Clone and Install OpenClaw

\`\`\`bash
git clone https://github.com/openclaw/openclaw.git
cd openclaw

# Create virtual environment
python3 -m venv venv
source venv/bin/activate

# Install dependencies
pip install -r requirements.txt
\`\`\`

---

## Step 3: Configure Environment Variables

Create a \`.env\` file in the project root:

\`\`\`env
# AI Provider
AI_PROVIDER=anthropic
ANTHROPIC_API_KEY=your_key_here

# Telegram
TELEGRAM_BOT_TOKEN=your_telegram_token
TELEGRAM_ALLOWED_USERS=your_telegram_user_id

# Agent Settings
AGENT_NAME=MyAgent
MAX_ITERATIONS=10
MEMORY_TYPE=sqlite
\`\`\`

---

## Step 4: Create Your Telegram Bot

1. Open Telegram → search **@BotFather**
2. Send \`/newbot\`
3. Choose a name and username for your bot
4. Copy the **API token** → paste into \`.env\`

Get your Telegram User ID:
1. Search **@userinfobot**
2. Send any message
3. Copy your numeric ID → paste into \`TELEGRAM_ALLOWED_USERS\`

---

## Step 5: Start OpenClaw

\`\`\`bash
# Activate virtual environment
source venv/bin/activate

# Start the agent
python main.py
\`\`\`

Your Telegram bot is now active! Send it a message to test:

\`\`\`
You: Search the web for the latest AI news and summarize the top 3 stories.
OpenClaw: 🔍 Searching... [browses web] Here are the top 3 AI stories today...
\`\`\`

---

## Step 6: Run as a Background Service

\`\`\`bash
# Create systemd service
sudo nano /etc/systemd/system/openclaw.service
\`\`\`

\`\`\`ini
[Unit]
Description=OpenClaw AI Agent
After=network.target

[Service]
User=ubuntu
WorkingDirectory=/home/ubuntu/openclaw
ExecStart=/home/ubuntu/openclaw/venv/bin/python main.py
Restart=always

[Install]
WantedBy=multi-user.target
\`\`\`

\`\`\`bash
sudo systemctl enable openclaw
sudo systemctl start openclaw
\`\`\`

---

## Expanding with Skills

OpenClaw's skill system lets you add new capabilities:

| Skill | What it does |
|---|---|
| \`web_search\` | DuckDuckGo / Google search |
| \`code_executor\` | Run Python/JS snippets |
| \`file_manager\` | Read/write local files |
| \`calendar\` | Google Calendar integration |
| \`email\` | Send emails via SMTP |

Add skills in \`config/skills.yaml\`:

\`\`\`yaml
enabled_skills:
  - web_search
  - code_executor
  - file_manager
\`\`\`

---

## Conclusion

OpenClaw gives you the power of an autonomous AI agent on infrastructure you own and control. Once running, it becomes an always-on assistant accessible through Telegram from anywhere in the world.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 8: Mastering Scheduled Tasks in Claude Code
  // ─────────────────────────────────────────────────────────
  "8": `
## Introduction

One of the most powerful — and underused — features of Claude Code is its ability to run **Scheduled Tasks**: autonomous jobs that execute on a timer, without a human typing a single prompt. This guide shows you how to configure, deploy, and manage scheduled tasks to build self-healing AI agents that work 24/7.

---

## What Are Scheduled Tasks?

Scheduled Tasks in Claude Code are predefined jobs that:

- Run at set intervals (hourly, daily, on-demand)
- Use the full Claude Code toolset (files, shell, git)
- Can create PRs, fix bugs, update docs, and send alerts
- Continue working even when your laptop is closed

Think of them as **AI cron jobs** with intelligence.

---

## Requirements

- Claude Code CLI installed (\`npm install -g @anthropic/claude-code\`)
- Anthropic API key with sufficient credits
- A GitHub repository connected to Claude Code
- A server or CI environment to host the scheduler (optional but recommended)

---

## Setting Up Your First Scheduled Task

### Step 1: Initialize Task Config

\`\`\`bash
claude tasks init
\`\`\`

This creates \`.claude/tasks.json\`:

\`\`\`json
{
  "tasks": []
}
\`\`\`

### Step 2: Define a Task

\`\`\`json
{
  "tasks": [
    {
      "id": "daily-test-check",
      "name": "Daily Test Health Check",
      "schedule": "0 7 * * *",
      "prompt": "Run the full test suite. If any tests fail, investigate the root cause, attempt a fix, and open a GitHub PR with your changes. If all tests pass, post a brief status update to the team Slack channel.",
      "tools": ["shell", "file", "github"],
      "timeout_minutes": 20
    }
  ]
}
\`\`\`

### Step 3: Deploy Tasks

\`\`\`bash
claude tasks deploy
\`\`\`

### Step 4: Monitor

\`\`\`bash
claude tasks status
claude tasks logs --id daily-test-check
\`\`\`

---

## Building a Self-Healing Agent

A self-healing agent automatically detects and fixes problems:

\`\`\`json
{
  "id": "self-healer",
  "name": "Production Self-Healer",
  "trigger": {
    "type": "webhook",
    "secret": "your-webhook-secret"
  },
  "prompt": "A production alert was triggered. 1) Check the error logs in /var/log/app.log. 2) Identify the root cause. 3) Apply a fix if possible. 4) Restart the affected service. 5) Verify the fix resolved the issue. 6) Write a post-mortem in docs/incidents/."
}
\`\`\`

Connect your monitoring tool (Datadog, PagerDuty) to fire this webhook when an alert triggers.

---

## Overcoming Hardware Limitations

Running scheduled tasks locally ties them to your machine's uptime. For true 24/7 operation:

### Option 1: GitHub Actions
\`\`\`yaml
# .github/workflows/claude-tasks.yml
name: Claude Scheduled Task
on:
  schedule:
    - cron: '0 7 * * *'
jobs:
  run-task:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      - name: Run Claude Task
        env:
          ANTHROPIC_API_KEY: \${{ secrets.ANTHROPIC_API_KEY }}
        run: |
          npx @anthropic/claude-code task run daily-test-check
\`\`\`

### Option 2: VPS Crontab
\`\`\`bash
# Add to crontab -e
0 7 * * * cd /path/to/repo && npx claude task run daily-test-check >> /var/log/claude-tasks.log 2>&1
\`\`\`

---

## Practical Task Examples

| Task | Schedule | What it does |
|---|---|---|
| Test Health Check | Daily 7 AM | Run tests, auto-fix failures |
| Dependency Update | Weekly Mon | \`npm update\`, run tests, open PR |
| Doc Freshness | Weekly Fri | Flag outdated docs |
| Security Scan | Daily 6 AM | Run \`npm audit\`, patch vulnerabilities |
| Issue Triage | Daily 9 AM | Label and respond to new GitHub issues |

---

## Conclusion

Scheduled Tasks transform Claude Code from a reactive assistant into a proactive teammate. Start with a simple daily test check, validate the quality of its output, then gradually delegate more complex recurring work to your AI scheduler.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 9: Claude Co-work Automation
  // ─────────────────────────────────────────────────────────
  "9": `
## Introduction

**Claude Co-work** represents a fundamental shift in how we interact with AI — from asking questions and receiving answers, to delegating tasks and receiving results. This guide explores how to use Claude Co-work to automate your daily workflow across file management, scheduling, research, and more.

---

## What is Claude Co-work?

Claude Co-work is Anthropic's collaborative workspace where Claude moves from **assistant** to **executor**. Instead of just advising you on how to do something, it does it for you:

| Mode | Example |
|---|---|
| Assistant | "Here's how to sort your email by sender..." |
| **Co-work** | *Actually sorts your email by sender* |

---

## Core Capabilities

### 1. File Management Automation

Tell Claude Co-work to reorganize your files:

> "Sort all PDFs in my Downloads folder by year (based on filename or content). Create subfolders for each year and move the files."

It will:
1. Scan the folder
2. Read file metadata
3. Create year folders
4. Move each file to the correct folder
5. Report what it did

### 2. Calendar & Scheduling

> "Look at my calendar for next week. Find all meetings longer than 1 hour and prepare a one-paragraph agenda for each based on the meeting title and attendees."

Claude Co-work integrates with Google Calendar and Outlook to read and write events directly.

### 3. Email Triage

> "Go through my unread emails from the last 48 hours. Flag anything that needs a reply today, draft responses for routine inquiries, and archive anything that's just a newsletter."

### 4. Research & Summarization

> "Research the top 5 project management tools for remote teams in 2026. Compare them across: pricing, integrations, mobile app quality, and AI features. Create a comparison table in a new Google Doc."

---

## Setting Up Claude Co-work

### Step 1: Access Co-work Mode

In Claude.ai, click **"Co-work"** in the sidebar (available on Pro and Team plans).

### Step 2: Grant Permissions

Connect the tools you want Claude to access:
- ☑ Google Drive
- ☑ Google Calendar
- ☑ Gmail
- ☑ Notion
- ☑ Slack

### Step 3: Define Your First Automation

Start simple with a clear, bounded task:

\`\`\`
Good first task:
"Every weekday morning, check my calendar for today's meetings 
and create a preparation note for each one in Notion."

Too vague:
"Help me be more productive."
\`\`\`

---

## Building a Daily Workflow Automation

Here's a complete morning routine automation:

\`\`\`
Morning Briefing Task (run daily at 8 AM):

1. Check my calendar — list today's meetings with times
2. Scan my email inbox — flag anything urgent (reply needed today)
3. Check my Notion task board — list tasks due today or overdue
4. Check the weather for my city
5. Compile all of the above into a clean "Morning Briefing" 
   note in Notion titled with today's date
\`\`\`

---

## Automation Patterns That Work Well

### The Recurring Report
Schedule Claude to generate a weekly status report from your project management tool every Friday at 4 PM.

### The Inbox Zero Assistant
Have Claude process your inbox daily — categorize, respond to routine messages, and surface only messages requiring your personal attention.

### The Meeting Prep Bot
Before every meeting, Claude reads the agenda, gathers relevant documents, and creates a structured prep note.

### The Learning Journal
Every evening, Claude reviews what you worked on (from calendar + task tool) and generates a brief learning journal entry.

---

## Tips for Best Results

1. **Be specific** — the more precise your task, the better the output
2. **Start with read-only** — let Claude observe before it acts
3. **Use approval gates** — review outputs before Claude sends emails or modifies files
4. **Iterate** — refine the task description based on first results
5. **Chain tasks** — connect multiple automations for end-to-end workflows

---

## Limitations

- Requires Claude Pro or Team plan
- Some integrations require OAuth permissions setup
- Complex multi-system tasks may require multiple runs
- Always review before sending external communications

---

## Conclusion

Claude Co-work shifts the AI relationship from consultant to colleague. The key is starting with well-defined, bounded tasks and gradually expanding scope as you build trust in the outputs. Within a week of using it consistently, you'll wonder how you managed your workflow without it.
  `,

  // ─────────────────────────────────────────────────────────
  // Post 10: Claude Code Offline with Ollama
  // ─────────────────────────────────────────────────────────
  "10": `
## Introduction

What if you could use Claude Code's powerful agentic coding workflow — for free, offline, with full privacy? That's exactly what running Claude Code with **Ollama** enables. By substituting the Anthropic API with a local LLM (like Qwen2.5-Coder), you get the full Claude Code experience without sending a single token to the cloud.

---

## Why Run Claude Code Offline?

| Benefit | Detail |
|---|---|
| 💰 Free | No API costs — run unlimited tokens locally |
| 🔒 Private | Your code never leaves your machine |
| ✈️ Offline | Works on planes, in remote areas, without internet |
| ⚡ Fast | No network latency for small models |

---

## Requirements

- **macOS or Linux** (Windows via WSL2)
- **16 GB RAM minimum** (32 GB recommended for larger models)
- **Ollama** installed
- **Claude Code CLI** installed
- At least **8 GB free disk space** per model

---

## Step 1: Install Ollama

\`\`\`bash
# macOS / Linux
curl -fsSL https://ollama.ai/install.sh | sh

# Verify installation
ollama --version
\`\`\`

---

## Step 2: Pull a Coding Model

The best free models for coding tasks:

| Model | Size | Best For |
|---|---|---|
| \`qwen2.5-coder:7b\` | 4.7 GB | Fast, everyday coding |
| \`qwen2.5-coder:14b\` | 9.0 GB | Better reasoning |
| \`qwen2.5-coder:32b\` | 19 GB | Complex tasks (32GB RAM needed) |
| \`codellama:13b\` | 7.4 GB | General coding alternative |
| \`deepseek-coder-v2\` | 8.9 GB | Strong at debugging |

\`\`\`bash
# Pull the recommended starter model
ollama pull qwen2.5-coder:7b

# Verify it's running
ollama run qwen2.5-coder:7b "Write a Python hello world"
\`\`\`

---

## Step 3: Start the Ollama API Server

\`\`\`bash
ollama serve
\`\`\`

This starts a local API at \`http://localhost:11434\` — compatible with the OpenAI API format.

---

## Step 4: Configure Claude Code to Use Ollama

Claude Code supports custom API endpoints via environment variables:

\`\`\`bash
# In your shell profile (.zshrc or .bashrc)
export ANTHROPIC_BASE_URL=http://localhost:11434/v1
export ANTHROPIC_API_KEY=ollama  # Any non-empty string works
export CLAUDE_MODEL=qwen2.5-coder:7b
\`\`\`

Reload your shell:
\`\`\`bash
source ~/.zshrc
\`\`\`

---

## Step 5: Run Claude Code with Ollama

\`\`\`bash
# Navigate to your project
cd ~/my-project

# Start Claude Code (it will use Ollama automatically)
claude

# Verify it's using the local model
/cost   # Should show $0.00 costs
\`\`\`

---

## Performance Comparison

| Task | Claude 3.5 Sonnet (API) | Qwen2.5-Coder 7B (Local) |
|---|---|---|
| Write a function | 2 sec | 4 sec |
| Refactor a file | 5 sec | 12 sec |
| Debug complex bug | 8 sec | 25 sec |
| Cost | ~$0.05 | $0.00 |

*Times vary based on hardware. M2/M3 Mac or GPU accelerates local inference significantly.*

---

## Optimizing Performance

### Enable GPU Acceleration (if available)

\`\`\`bash
# Ollama auto-detects GPU (NVIDIA, AMD, Apple Silicon)
# Check if GPU is being used
ollama ps
\`\`\`

### Use Metal on Apple Silicon

Ollama automatically uses Apple's Metal GPU on M1/M2/M3 Macs, giving 3–5x speedup over CPU-only.

### Adjust Context Length

\`\`\`bash
# In Modelfile
PARAMETER num_ctx 8192
\`\`\`

---

## Limitations vs. Cloud Claude Code

| Feature | Cloud (Anthropic) | Local (Ollama) |
|---|---|---|
| Intelligence | ⭐⭐⭐⭐⭐ | ⭐⭐⭐ |
| Speed | Fast | Model-dependent |
| Cost | Pay per token | Free |
| Privacy | Data sent to Anthropic | 100% local |
| Internet required | Yes | No |
| Latest features | Always | May lag |

---

## Best Use Cases for Offline Mode

- **Airplane coding** — no internet, full AI assistance
- **Sensitive codebases** — proprietary code that can't leave your network
- **Cost-conscious development** — side projects with no AI budget
- **Learning** — experiment freely without worrying about token costs

---

## Conclusion

Running Claude Code with Ollama is a game-changer for developers who value privacy, cost control, or offline capability. While the local models aren't quite as capable as Claude 3.5 Sonnet, they're remarkably good for most everyday coding tasks — and the price can't be beat.

Start with **qwen2.5-coder:7b** for a fast, capable experience, and upgrade to the 14B or 32B model if you have the RAM and need stronger reasoning.
  `,

};

export default blogDetailsData;
